<template>
  <div v-if="viewState?.kind === 'images'" class="asin-stack">
    <div class="asin-stack-avatars" aria-hidden="true">
      <div
        v-for="(url, i) in viewState.visibleUrls || []"
        :key="`img-${record?.id || ''}-${i}-${url}`"
        class="asin-stack-item"
        :style="{ zIndex: i + 1 }"
      >
        <img :src="url" alt="" />
      </div>
      <div
        v-if="(viewState.more || 0) > 0"
        class="asin-stack-item asin-stack-more"
        :style="{ zIndex: (viewState.visibleUrls || []).length + 1 }"
      >
        +{{ viewState.more }}
      </div>
    </div>
  </div>
  <div v-else-if="viewState?.kind === 'countOnly'" class="asin-stack-count-only">
    <span>{{ viewState.total }} ASINs</span>
  </div>
  <span v-else-if="record?.asin" class="asin-stack-fallback">{{ record.asin }}</span>
  <span v-else class="asin-stack-empty">—</span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  record: {
    type: Object,
    default: () => ({}),
  },
  visibleCount: {
    type: Number,
    default: 3,
  },
})

const asinStackProductList = (record) => {
  const raw =
    record?.products ??
    record?.asinProducts ??
    record?.productList ??
    record?.productThumbnails ??
    []
  return Array.isArray(raw) ? raw : []
}

const isUsableProductImageUrl = (raw) => {
  const u = String(raw ?? '').trim()
  if (!u || u === 'string') return false
  return /^https?:\/\//i.test(u) || u.startsWith('//') || u.startsWith('data:')
}

const asinStackImageUrls = (record) => {
  const arr = asinStackProductList(record)
  const urls = []
  const seen = new Set()
  for (const p of arr) {
    const u = String(p?.imageUrl || p?.productImage || p?.image || '').trim()
    if (!u || seen.has(u)) continue
    seen.add(u)
    if (isUsableProductImageUrl(u)) urls.push(u)
  }
  return urls
}

const asinStackTotalCount = (record) => {
  const products = asinStackProductList(record)
  const fromList = products.length
  const urls = asinStackImageUrls(record)
  const n = Number(
    record?.productCount ??
      record?.productsCount ??
      record?.totalProducts ??
      record?.asinCount ??
      0,
  )
  if (fromList > 0) {
    if (Number.isFinite(n) && n > 0) return Math.max(n, fromList)
    return fromList
  }
  if (Number.isFinite(n) && n > 0) return Math.max(n, urls.length)
  return urls.length
}

const viewState = computed(() => {
  const record = props.record
  const urls = asinStackImageUrls(record)
  const total = asinStackTotalCount(record)
  if (total <= 0) return { kind: 'none' }
  const displayCount = Math.max(1, Number(props.visibleCount) || 3)
  const visibleUrls = urls.slice(0, displayCount)
  const more = Math.max(0, total - displayCount)
  if (!visibleUrls.length) return { kind: 'countOnly', total, more }
  return { kind: 'images', visibleUrls, more, total }
})
</script>

<style scoped lang="less">
.asin-stack {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
}

.asin-stack-avatars {
  display: inline-flex;
  align-items: center;
  padding-left: 2px;
}

.asin-stack-item {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  box-sizing: border-box;
  overflow: hidden;
  margin-left: -10px;
  flex-shrink: 0;
  background: #f3f4f6;
  box-shadow: 0 0 0 1px rgba(15, 6, 41, 0.06);

  &:first-child {
    margin-left: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.asin-stack-more {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Sora;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: rgba(87, 81, 105, 1);
  background: rgba(241, 240, 245, 1);
}

.asin-stack-empty,
.asin-stack-fallback {
  color: #9ca3af;
  font-size: 13px;
}

.asin-stack-fallback {
  color: #374151;
}

.asin-stack-count-only {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: default;
}
</style>
